import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Banknote, Calendar, Clock,
} from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';

import {
    fetchPendingCashVouchers,
    fetchCashVoucherById,
    submitCashVoucherVerification,
    clearVoucherDetail,
    clearVerifyResult,
    selectPendingVouchers,
    selectSelectedVoucherDetail,
    selectPendingVouchersLoading,
    selectVoucherDetailLoading,
    selectVerifyLoading,
    selectPendingVouchersError,
} from '../../slices/accountsSlice/cashVoucherSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

// Compact read-only field: label / value / optional green name line (legacy shows names under codes)
const Field = ({ label, value, sub, className = '' }) => (
    <div className={`min-w-0 ${className}`}>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">{label}</p>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug break-words">{value || '—'}</p>
        {sub && <p className="text-[11px] italic text-green-700 dark:text-green-400 leading-tight break-words">{sub}</p>}
    </div>
);

const VerifyCashVoucher = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    const pendingVouchers = useSelector(selectPendingVouchers);
    const voucherDetail   = useSelector(selectSelectedVoucherDetail);
    const listLoading     = useSelector(selectPendingVouchersLoading);
    const detailLoading   = useSelector(selectVoucherDetailLoading);
    const verifyLoading   = useSelector(selectVerifyLoading);
    const listError       = useSelector(selectPendingVouchersError);

    const remarks         = useSelector(selectRemarks);
    const remarksLoading  = useSelector(selectRemarksLoading);

    const statusLoading   = useSelector(selectStatusListLoading);
    const statusError     = useSelector(selectStatusListError);
    const enabledActions  = useSelector(selectEnabledActions);
    const hasActions      = useSelector(selectHasActions);

    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;

    const [selectedItem, setSelectedItem]               = useState(null);
    const [isVerified, setIsVerified]                   = useState(false);
    const [verificationComment, setVerificationComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory]   = useState(false);
    const [searchQuery, setSearchQuery]                 = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered]   = useState(false);

    const { InboxTitle, ModuleDisplayName, RoleId } = notificationData || {};

    const currentUser = userData?.userName || 'system';
    const currentUID  = userData?.UID || userData?.uid || 0;

    const formatAmount = (val) => {
        const n = parseFloat(val);
        if (isNaN(n)) return '0.00';
        return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // ── Init ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (RoleId) dispatch(fetchPendingCashVouchers(RoleId));
    }, [RoleId, dispatch]);

    useEffect(() => {
        // Legacy (VerifyGeneralpayablebycashView) strips 'Return' from the status dropdown
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(clearVoucherDetail());
            dispatch(resetApprovalData());
            dispatch(clearVerifyResult());
        };
    }, [dispatch]);

    // ── On item select → fetch detail ────────────────────────────────────────
    useEffect(() => {
        if (selectedItem) {
            dispatch(clearVoucherDetail());
            dispatch(resetApprovalData());
            dispatch(fetchCashVoucherById(selectedItem.Voucherno));
            setIsVerified(false);
            setVerificationComment('');
            setShowRemarksHistory(false);
        }
    }, [selectedItem, dispatch]);

    // ── On detail load → fetch status list + remarks ─────────────────────────
    useEffect(() => {
        if (voucherDetail?.MOID && RoleId) {
            dispatch(fetchStatusList({
                MOID: voucherDetail.MOID,
                ROID: RoleId,
                ChkAmt: parseFloat(voucherDetail.Amount) || 0,
            }));
            dispatch(setSelectedMOID(voucherDetail.MOID));
            dispatch(fetchRemarks({
                trno: voucherDetail.Voucherno || selectedItem?.Voucherno || '',
                moid: voucherDetail.MOID,
            }));
        }
    }, [voucherDetail?.MOID, RoleId, dispatch]);

    // ── Collapse left panel when item selected ───────────────────────────────
    useEffect(() => {
        if (selectedItem) setIsLeftPanelCollapsed(true);
    }, [selectedItem]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleItemSelect = (item) => {
        setSelectedItem(item);
    };

    const handleRefresh = () => {
        if (RoleId) dispatch(fetchPendingCashVouchers(RoleId));
    };

    const handleActionClick = async (action) => {
        if (!selectedItem) { toast.error('No voucher selected'); return; }
        if (!verificationComment.trim()) {
            toast.error('Verification comment is mandatory before proceeding.');
            return;
        }
        if (!isVerified) {
            toast.error('Please verify the voucher details by checking the verification checkbox.');
            return;
        }

        let actionValue = action.value || action.type || '';
        if (!actionValue.trim()) {
            const map = { approve: 'Approve', verify: 'Verify', reject: 'Reject', return: 'Return' };
            actionValue = map[action.type?.toLowerCase()] || 'Verify';
        }

        const payload = {
            Voucherno:       voucherDetail?.Voucherno || selectedItem.Voucherno,
            ApprovalRemarks: verificationComment.trim(),
            Approvalstatus:  actionValue,
            RoleID:          RoleId || 0,
            Createdby:       currentUser,
            // Legacy fills UID from the approver's session, not from the voucher
            UID:             currentUID,
            CID:             voucherDetail?.CID || selectedItem.CID || 0,
        };

        try {
            const res = await dispatch(submitCashVoucherVerification(payload)).unwrap();
            // SP returns "Submitted,<VoucherId>" on success, otherwise an error message (HTTP 200 either way)
            const raw   = typeof res?.Data === 'string' ? res.Data : (typeof res === 'string' ? res : '');
            const parts = raw.split(',');
            if (parts[0].trim().toLowerCase() !== 'submitted') {
                toast.error(raw || 'Error Occurred While General Payment by cash Verification', { autoClose: 10000 });
                return;
            }
            const successMsg = {
                verify:  'Verified Successfully',
                approve: 'Approved Successfully',
                reject:  'Rejected Successfully',
            }[actionValue.toLowerCase()] || `${actionValue} completed successfully`;
            toast.success(actionValue.toLowerCase() === 'approve' && parts[1]
                ? `Cash Voucher: ${successMsg}. Voucher Id is ${parts[1].trim()}`
                : `Cash Voucher: ${successMsg}`);
            setTimeout(() => {
                dispatch(fetchPendingCashVouchers(RoleId));
                dispatch(clearVoucherDetail());
                dispatch(clearVerifyResult());
                dispatch(resetApprovalData());
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
            }, 800);
        } catch (err) {
            toast.error(typeof err === 'string' ? err : (err?.message || 'Action failed'), { autoClose: 10000 });
        }
    };

    // ── Filtered list ─────────────────────────────────────────────────────────
    const filteredItems = pendingVouchers.filter(v =>
        !searchQuery ||
        v.Voucherno?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.DCACode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.SelfCCCode?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isGST = voucherDetail?.PaymentType === 'GST';

    // ── Left panel item renderers ─────────────────────────────────────────────
    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/40 dark:to-purple-900/40 border-2 border-indigo-200 dark:border-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Banknote className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.Name || '—'}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.SelfCCCode}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between">
                    <span className="font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded truncate">{item.Voucherno}</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">₹{formatAmount(item.Amount)}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" /><span>{item.PaymentDate}</span>
                    </span>
                    <span className="truncate max-w-[100px] text-gray-500">{item.DCACode}</span>
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px]">{item.Name || '—'}</span>
            <span className="font-mono text-indigo-600 dark:text-indigo-400 min-w-[110px]">{item.Voucherno}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.SelfCCCode}</span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]">
                <Calendar className="w-3 h-3" /> {item.PaymentDate}
            </span>
            <span className="ml-auto font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">₹{formatAmount(item.Amount)}</span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center">
            <Banknote className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    // ── Detail content ────────────────────────────────────────────────────────
    const renderDetailContent = () => {
        if (!selectedItem) return null;

        const isOtherCC = !!voucherDetail?.PaidAgainstCCCode;
        const hasIGST   = voucherDetail?.IGSTAmount !== null && voucherDetail?.IGSTAmount !== undefined;

        return (
            <div className="space-y-2">
                {detailLoading && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg px-3 py-2 border border-indigo-200 dark:border-indigo-700 flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                        <span className="text-indigo-700 dark:text-indigo-400 text-xs">Loading voucher details...</span>
                    </div>
                )}

                {voucherDetail && (
                    <>
                        {/* Compact summary strip */}
                        <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg px-3 py-2 border border-indigo-200 dark:border-indigo-700">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
                                    <Banknote className="w-4 h-4 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <h2 className="text-sm font-bold text-gray-900 dark:text-white truncate">{voucherDetail.Name || '—'}</h2>
                                    <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                                        <span className="font-mono text-indigo-600 dark:text-indigo-400">{voucherDetail.Voucherno}</span>
                                        {voucherDetail.PaymentType && (
                                            <span className={`px-1.5 py-px rounded-full font-medium ${isGST ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'}`}>
                                                {voucherDetail.PaymentType}
                                            </span>
                                        )}
                                        <span className="px-1.5 py-px rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium">
                                            {isOtherCC ? 'Debit from Other CC' : 'Self Debit'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 leading-tight">Total Amount</p>
                                <p className="text-base font-bold text-indigo-600 dark:text-indigo-400 leading-tight">₹{formatAmount(voucherDetail.Amount)}</p>
                            </div>
                        </div>

                        {/* All voucher fields in one dense card */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2.5">
                                <Field label="Self Cost Center" value={voucherDetail.SelfCCCode} sub={voucherDetail.SelfCCName} />
                                {isOtherCC && (
                                    <Field label="Other Cost Center" value={voucherDetail.PaidAgainstCCCode} sub={voucherDetail.PaidAgainstCCName} />
                                )}
                                <Field label="Invoice Date" value={voucherDetail.InvoiceDate} />
                                <Field label="Paid Date" value={voucherDetail.PaymentDate} />
                                <Field label="Account Head" value={voucherDetail.DCACode} sub={voucherDetail.DCAName} />
                                <Field label="Sub Account Head" value={voucherDetail.SubDCACode} sub={voucherDetail.SubDCAName} />
                                <Field label="Name" value={voucherDetail.Name} className={isOtherCC ? 'col-span-2' : ''} />
                                {isGST && (
                                    <>
                                        <Field label="GST No" value={voucherDetail.GSTNo} />
                                        <Field label="Invoice Amount" value={`₹${formatAmount(voucherDetail.InvoiceAmount)}`} />
                                        {hasIGST ? (
                                            <Field label="IGST Amount" value={`₹${formatAmount(voucherDetail.IGSTAmount)}`} />
                                        ) : (
                                            <>
                                                <Field label="CGST Amount" value={`₹${formatAmount(voucherDetail.CGSTAmount)}`} />
                                                <Field label="SGST Amount" value={`₹${formatAmount(voucherDetail.SGSTAmount)}`} />
                                            </>
                                        )}
                                    </>
                                )}
                                <Field label="Remarks" value={voucherDetail.Remarks} className="col-span-2 lg:col-span-4" />
                            </div>
                        </div>

                        <RemarksHistory
                            isOpen={showRemarksHistory}
                            onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                            remarks={remarks}
                            loading={remarksLoading}
                            title="Approval History"
                            className="!p-2 !rounded-lg"
                        />

                        <VerificationInput
                            isVerified={isVerified}
                            onVerifiedChange={setIsVerified}
                            comment={verificationComment}
                            onCommentChange={(e) => setVerificationComment(e.target.value)}
                            className="!space-y-2"
                            config={{
                                checkboxLabel: '✓ I have verified all cash voucher details (payee, amount, cost center, DCA, dates)',
                                checkboxDescription: '',
                                commentLabel: 'Verification Comments',
                                commentPlaceholder: 'Please verify the payee, cost center allocation, amount, and any discrepancies...',
                                commentRequired: true,
                                commentRows: 2,
                                commentMaxLength: 1000,
                                showCharCount: true,
                                validationStyle: 'dynamic',
                                checkboxContainerClass: 'px-3 py-2',
                                commentContainerClass: 'px-3 py-2',
                                checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                                commentGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                                commentBorder: 'border-indigo-200 dark:border-indigo-700',
                            }}
                        />

                        {statusLoading ? (
                            <div className="flex items-center justify-center space-x-2 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">Loading actions...</span>
                            </div>
                        ) : statusError ? (
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 border border-red-200 dark:border-red-700 text-center">
                                <p className="text-xs text-red-600 dark:text-red-400">⚠️ Error loading actions: {statusError}</p>
                            </div>
                        ) : !hasActions || !enabledActions?.length ? (
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg px-3 py-2 border border-yellow-200 dark:border-yellow-700 text-center">
                                <p className="text-yellow-700 dark:text-yellow-400 text-xs">ℹ️ No actions available for this voucher</p>
                            </div>
                        ) : (
                            <ActionButtons
                                actions={enabledActions}
                                onActionClick={handleActionClick}
                                loading={verifyLoading}
                                isVerified={isVerified}
                                comment={verificationComment}
                                showValidation={false}
                                excludeActions={['send back']}
                                className="[&>div]:!gap-2 [&_button]:!py-2 [&_button]:!min-h-[40px] [&_button]:!text-sm"
                            />
                        )}
                    </>
                )}
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-2">
            <InboxHeader
                title={`${InboxTitle || 'Cash Voucher Verification'} (${pendingVouchers.length})`}
                subtitle={ModuleDisplayName}
                itemCount={pendingVouchers.length}
                onBackClick={() => onNavigate?.('dashboard', { name: 'Dashboard', type: 'dashboard' })}
                HeaderIcon={Banknote}
                badgeText="Cash Voucher"
                badgeCount={pendingVouchers.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by voucher no, payee, DCA, CC...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: listLoading,
                    error: listError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No cash vouchers pending',
                        itemKey: 'CID',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                    },
                    renderPopupContent: (_item) => renderDetailContent(),
                    popupConfig: {
                        title: 'Cash Voucher Verification',
                        icon: Banknote,
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: detailLoading,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Voucher Details',
                        icon: Banknote,
                        selectedTitle: 'Voucher Verification',
                        emptyTitle: 'No Voucher Selected',
                        emptyMessage: 'Select a cash voucher from the list to view details and verify.',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxHeight: 'calc(100vh - 140px)',
                        sticky: true,
                        stickyTop: '0.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyCashVoucher;
